import React from 'react';
import FormField from '../shared/FormField';
import formDataStorage from '../../utils/formDataStorage';
import { Heart, Activity, Shield } from 'lucide-react';
// import telecode from 'chinese-telecode' // 未使用



// 补充信息1 - 健康状况表单
const Part1Form = ({ data, onChange }) => {

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    const savedData = formDataStorage.loadFormData('part1');
    
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }
  }, [data, onChange]); // 当data或onChange变化时重新运行

  // 获取问题的当前值
  const getQuestionValue = (key) => {
    const questionData = data[key];
    if (questionData && typeof questionData === 'object' && 'value' in questionData) {
      return questionData.value;
    }
    // 兼容旧的数据格式
    if (data[key] !== undefined) {
      return data[key];
    }
    // 如果数据未初始化，默认返回 false
    return false;
  };

  // 获取问题的解释文本
  const getQuestionExplanation = (key) => {
    const questionData = data[key];
    if (questionData && typeof questionData === 'object' && 'explanation' in questionData) {
      return questionData.explanation || '';
    }
    return '';
  };

  // 处理是否选择的变化
  const handleYesNoChange = (key, value) => {
    const boolValue = value === 'true';
    const currentData = data[key];
    
    // 如果当前数据是对象格式，更新value字段
    if (currentData && typeof currentData === 'object' && 'value' in currentData) {
      const newData = {
        ...currentData,
        value: boolValue,
        explanation: boolValue ? currentData.explanation : ''
      };
      onChange(key, newData);
    } else {
      // 兼容旧格式，创建新的对象格式
      const newData = {
        value: boolValue,
        explanation: ''
      };
      onChange(key, newData);
    }
  };

  // 处理解释文本的变化
  const handleExplanationChange = (key, explanation) => {
    const currentData = data[key];
    
    // 如果当前数据是对象格式，更新explanation字段
    if (currentData && typeof currentData === 'object' && 'value' in currentData) {
      const newData = {
        ...currentData,
        explanation: explanation
      };
      onChange(key, newData);
    } else {
      // 兼容旧格式，创建新的对象格式
      const newData = {
        value: currentData,
        explanation: explanation
      };
      onChange(key, newData);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-red-50 to-pink-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">❤️</span>
          健康状况信息
        </h3>

        {/* 重要提示 */}
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
          <div className="flex items-start">
            <Shield className="w-6 h-6 text-orange-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-orange-800">
              <p className="font-bold mb-2 text-base">重要提示</p>
              <p className="mb-2">以下问题涉及健康状况和医疗信息。请务必如实回答所有问题。</p>
              <p className="font-semibold">任何虚假陈述都可能导致：</p>
              <ul className="list-disc list-inside mt-1 ml-2">
                <li>签证申请被拒绝</li>
                <li>面临法律起诉</li>
                <li>被禁止入境美国</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 传染病问题 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Activity className="w-5 h-5 mr-2 text-red-600" />
              您是否患有涉及公共卫生的传染病？
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              包括但不限于：软下疳、淋病、腹股沟肉芽肿、传染性麻风病、性病性淋巴肉芽肿、传染期梅毒、活动性肺结核等
            </p>
            
            <div>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="disease"
                    value="false"
                    checked={getQuestionValue('disease') === false}
                    onChange={(e) => {
                      handleYesNoChange('disease', 'false');
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">❌ 否 - 我没有传染病</span>
                </label>
                
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="disease"
                    value="true"
                    checked={getQuestionValue('disease') === true}
                    onChange={(e) => handleYesNoChange('disease', 'true')}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">✅ 是 - 我有传染病</span>
                </label>
              </div>
            </div>
          </div>

          {getQuestionValue('disease') === true && (
            <div className="mt-4 space-y-3">
              {/* 强烈风险警告 */}
              <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-pulse">
                <div className="flex items-start">
                  <Shield className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-bold text-base mb-2">⚠️ 重要风险警告</p>
                    <p className="mb-2">
                      <span className="font-bold">选择"是"将大幅增加拒签风险！</span>
                      此选项可能导致：
                    </p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li className="font-semibold">面签时遭到严格审查</li>
                      <li className="font-semibold">签证申请被拒绝</li>
                      <li className="font-semibold">可能面临永久拒签</li>
                    </ul>
                    <p className="mt-3 font-bold text-red-900 bg-red-100 p-2 rounded">
                      💡 强烈建议：如无必要，请仔细核实后选择"否"。如确实存在相关情况，请立即咨询专业移民律师！
                    </p>
                  </div>
                </div>
              </div>

              <FormField
                label="请详细说明您的健康状况"
                type="textarea"
                value={getQuestionExplanation('disease')}
                onChange={(e) => handleExplanationChange('disease', e.target.value)}
                placeholder="请说明疾病类型、诊断日期、治疗情况、当前状态等信息..."
                required
                rows={4}
              />
            </div>
          )}
        </div>

        {/* 精神/身体疾病问题 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Shield className="w-5 h-5 mr-2 text-orange-600" />
              您是否患有对其他人的人身安全及利益造成威胁的精神或身体疾病？
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              包括可能影响他人安全的任何精神健康状况或身体疾病
            </p>
            
            <div>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="disorder"
                    value="false"
                    checked={getQuestionValue('disorder') === false}
                    onChange={(e) => {
                      handleYesNoChange('disorder', 'false');
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">❌ 否 - 我没有此类疾病</span>
                </label>
                
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="disorder"
                    value="true"
                    checked={getQuestionValue('disorder') === true}
                    onChange={(e) => handleYesNoChange('disorder', 'true')}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">✅ 是 - 我有此类疾病</span>
                </label>
              </div>
            </div>
          </div>

          {getQuestionValue('disorder') === true && (
            <div className="mt-4 space-y-3">
              {/* 强烈风险警告 */}
              <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-pulse">
                <div className="flex items-start">
                  <Shield className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-bold text-base mb-2">⚠️ 重要风险警告</p>
                    <p className="mb-2">
                      <span className="font-bold">选择"是"将大幅增加拒签风险！</span>
                      此选项可能导致：
                    </p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li className="font-semibold">面签时遭到严格审查</li>
                      <li className="font-semibold">签证申请被拒绝</li>
                      <li className="font-semibold">可能面临永久拒签</li>
                    </ul>
                    <p className="mt-3 font-bold text-red-900 bg-red-100 p-2 rounded">
                      💡 强烈建议：如无必要，请仔细核实后选择"否"。如确实存在相关情况，请立即咨询专业移民律师！
                    </p>
                  </div>
                </div>
              </div>

              <FormField
                label="请详细说明您的状况"
                type="textarea"
                value={getQuestionExplanation('disorder')}
                onChange={(e) => handleExplanationChange('disorder', e.target.value)}
                placeholder="请说明疾病类型、诊断日期、治疗情况、当前状态等信息..."
                required
                rows={4}
              />
            </div>
          )}
        </div>

        {/* 药物滥用问题 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Heart className="w-5 h-5 mr-2 text-purple-600" />
              您是否曾经滥用药物并上瘾？
            </h4>
            <p className="text-sm text-gray-600 mb-4">
              包括处方药物滥用、非法药物使用或任何成瘾性物质的滥用
            </p>
            
            <div>
              <div className="flex gap-6">
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="druguser"
                    value="false"
                    checked={getQuestionValue('druguser') === false}
                    onChange={(e) => {
                      handleYesNoChange('druguser', 'false');
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">❌ 否 - 我没有药物滥用历史</span>
                </label>
                
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="druguser"
                    value="true"
                    checked={getQuestionValue('druguser') === true}
                    onChange={(e) => handleYesNoChange('druguser', 'true')}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                  />
                  <span className="text-base font-medium text-gray-700">✅ 是 - 我有药物滥用历史</span>
                </label>
              </div>
            </div>
          </div>

          {getQuestionValue('druguser') === true && (
            <div className="mt-4 space-y-3">
              {/* 强烈风险警告 */}
              <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4 animate-pulse">
                <div className="flex items-start">
                  <Shield className="w-6 h-6 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-red-800">
                    <p className="font-bold text-base mb-2">⚠️ 重要风险警告</p>
                    <p className="mb-2">
                      <span className="font-bold">选择"是"将大幅增加拒签风险！</span>
                      此选项可能导致：
                    </p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li className="font-semibold">面签时遭到严格审查</li>
                      <li className="font-semibold">签证申请被拒绝</li>
                      <li className="font-semibold">可能面临永久拒签</li>
                    </ul>
                    <p className="mt-3 font-bold text-red-900 bg-red-100 p-2 rounded">
                      💡 强烈建议：如无必要，请仔细核实后选择"否"。如确实存在相关情况，请立即咨询专业移民律师！
                    </p>
                  </div>
                </div>
              </div>

              <FormField
                label="请详细说明您的情况"
                type="textarea"
                value={getQuestionExplanation('druguser')}
                onChange={(e) => handleExplanationChange('druguser', e.target.value)}
                placeholder="请说明药物类型、使用时期、治疗情况、康复状态等信息..."
                required
                rows={4}
              />
            </div>
          )}
        </div>

        {/* 信息提示 */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <div className="flex items-start">
            <Shield className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-blue-800 mb-1">隐私保护</h4>
              <p className="text-sm text-blue-700">
                您提供的健康信息将严格保密，仅用于签证申请评估。某些健康状况可能需要额外的医疗检查或文件。
                请注意，即使存在健康问题，也不一定会导致签证申请被拒绝。重要的是提供准确和完整的信息。
              </p>
            </div>
          </div>
        </div>

        {/* 预览信息 */}
        <div className="mt-6 p-4 bg-gradient-to-r from-pink-50 to-red-50 rounded-lg border-l-4 border-pink-400 shadow-sm">
          <h4 className="font-semibold text-pink-800 mb-2">信息预览</h4>
          <div className="text-sm text-pink-700 space-y-2">
            <div className="flex items-center">
              <span className="font-medium mr-2">传染性疾病:</span>
              <span className={getQuestionValue('disease') ? 'text-red-600 font-semibold' : 'text-green-600'}>
                {getQuestionValue('disease') ? '是' : '否'}
              </span>
              {getQuestionValue('disease') && getQuestionExplanation('disease') && (
                <span className="ml-2 text-xs text-gray-600">（已填写说明）</span>
              )}
            </div>
            
            <div className="flex items-center">
              <span className="font-medium mr-2">精神/身体疾病:</span>
              <span className={getQuestionValue('disorder') ? 'text-orange-600 font-semibold' : 'text-green-600'}>
                {getQuestionValue('disorder') ? '是' : '否'}
              </span>
              {getQuestionValue('disorder') && getQuestionExplanation('disorder') && (
                <span className="ml-2 text-xs text-gray-600">（已填写说明）</span>
              )}
            </div>
            
            <div className="flex items-center">
              <span className="font-medium mr-2">药物滥用历史:</span>
              <span className={getQuestionValue('druguser') ? 'text-purple-600 font-semibold' : 'text-green-600'}>
                {getQuestionValue('druguser') ? '是' : '否'}
              </span>
              {getQuestionValue('druguser') && getQuestionExplanation('druguser') && (
                <span className="ml-2 text-xs text-gray-600">（已填写说明）</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Part1Form; 
